import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "./firebase";
import RazorpayButton from "./RazorpayButton";
import { CATEGORY_ICON_IMAGES, MAJOR_INDIAN_CITIES, SERVICE_CATEGORIES } from "./serviceCatalog";

const CITY_ALIASES = {
  bangalore: "bengaluru",
  bombay: "mumbai",
  calcutta: "kolkata",
  trivandrum: "thiruvananthapuram",
  baroda: "vadodara",
};

const CATEGORY_ALIASES = {
  vet: "veterinary & health care",
  veterinary: "veterinary & health care",
  doctor: "veterinary & health care",
  grooming: "grooming, wellness & spa",
  spa: "grooming, wellness & spa",
  boarding: "boarding, daycare & sitting",
  hotel: "boarding, daycare & sitting",
  daycare: "boarding, daycare & sitting",
  walking: "walking, training & behaviour",
  training: "walking, training & behaviour",
  behaviour: "walking, training & behaviour",
  behavior: "walking, training & behaviour",
  food: "food, nutrition & meals",
  nutrition: "food, nutrition & meals",
  meals: "food, nutrition & meals",
  transport: "transport, travel & relocation",
  travel: "transport, travel & relocation",
  relocation: "transport, travel & relocation",
  adoption: "adoption, rescue & community",
  rescue: "adoption, rescue & community",
  store: "stores, pharmacy & accessories",
  pharmacy: "stores, pharmacy & accessories",
  accessories: "stores, pharmacy & accessories",
  breeding: "breeding & registration",
  registration: "breeding & registration",
  photography: "photography, art & events",
  art: "photography, art & events",
  events: "photography, art & events",
  memorial: "memorial",
  cleaning: "cleaning & home support",
};

const CATEGORY_HERO_STYLES = {
  "Veterinary & Health Care": {
    accent: "#1d4ed8",
    accentSoft: "#bcd7ff",
    label: "V",
  },
  "Grooming, Wellness & Spa": {
    accent: "#0f766e",
    accentSoft: "#c7f9f2",
    label: "G",
  },
  "Boarding, Daycare & Sitting": {
    accent: "#b45309",
    accentSoft: "#fde7c4",
    label: "B",
  },
  "Walking, Training & Behaviour": {
    accent: "#7c3aed",
    accentSoft: "#e9ddff",
    label: "T",
  },
  "Food, Nutrition & Meals": {
    accent: "#0ea5e9",
    accentSoft: "#dbeffd",
    label: "N",
  },
  "Transport, Travel & Relocation": {
    accent: "#f97316",
    accentSoft: "#ffe0c2",
    label: "R",
  },
  "Insurance & Protection": {
    accent: "#4f46e5",
    accentSoft: "#e0e7ff",
    label: "I",
  },
  "Adoption, Rescue & Community": {
    accent: "#16a34a",
    accentSoft: "#dcfce7",
    label: "A",
  },
  "Stores, Pharmacy & Accessories": {
    accent: "#db2777",
    accentSoft: "#fce7f3",
    label: "S",
  },
  "Breeding & Registration": {
    accent: "#9333ea",
    accentSoft: "#ede9fe",
    label: "BR",
  },
  "Photography, Art & Events": {
    accent: "#f59e0b",
    accentSoft: "#fde68a",
    label: "P",
  },
  "Memorial": {
    accent: "#0f172a",
    accentSoft: "#e2e8f0",
    label: "M",
  },
  "Cleaning & Home Support": {
    accent: "#059669",
    accentSoft: "#d1fae5",
    label: "C",
  },
};

const PRIMARY_BLUE = "#1B3DB5";
const HEADING_COLOR = "#1A1A2E";
const META_COLOR = "#888888";

const PRIMARY_BUTTON_STYLE = {
  background: PRIMARY_BLUE,
  color: "#fff",
  border: "none",
  borderRadius: 50,
  padding: "12px 24px",
  fontWeight: 700,
  cursor: "pointer",
};

const SECONDARY_BUTTON_STYLE = {
  background: "#fff",
  color: PRIMARY_BLUE,
  border: `2px solid ${PRIMARY_BLUE}`,
  borderRadius: 50,
  padding: "12px 24px",
  fontWeight: 700,
  cursor: "pointer",
};

const faIcon = (name) =>
  `https://api.iconify.design/fa6-solid/${name}.svg?color=%231B3DB5&width=40&height=40`;

const SERVICE_TYPE_ICON_MAP = {
  "Home Vet Visits": faIcon("house-medical"),
  "Online Vet Consultation": faIcon("video"),
  "Emergency Vet Service": faIcon("truck-medical"),
  "Vaccination Service": faIcon("syringe"),
  "Pet Testing": faIcon("flask"),
  "Pet DNA Testing": faIcon("dna"),
  "Pet Physiotherapy": faIcon("person-walking"),
  "Pet Rehabilitation": faIcon("heart-pulse"),
  "Pet Boarding for Medical Care": faIcon("bed-pulse"),
  "Pet Microchipping": faIcon("microchip"),
  "Pet Grooming": faIcon("shower"),
  "Mobile Pet Grooming": faIcon("scissors"),
  "Pet Massage & Spa": faIcon("spa"),
  "Pet Hotel": faIcon("hotel"),
  "Pet Daycare": faIcon("sun"),
  "Pet Sitting": faIcon("house-user"),
  "Pet Boarding": faIcon("moon"),
  "Pet Walking": faIcon("person-walking"),
  "Pet Training": faIcon("graduation-cap"),
  "Pet Behaviour Training": faIcon("medal"),
  "Dog Behaviourist": faIcon("brain"),
  "Pet First Aid Training": faIcon("flag-checkered"),
  "Dog Food Service": faIcon("bowl-food"),
  "Pet Food Delivery": faIcon("prescription-bottle"),
  "Pet Meal Subscription": faIcon("cookie"),
  "Pet Nutrition": faIcon("apple-whole"),
  "Pet Nutritionist Consultation": faIcon("clipboard-list"),
  "Pet Transport": faIcon("taxi"),
  "Pet Taxi": faIcon("taxi"),
  "Pet Airport Transport": faIcon("plane"),
  "Pet Travel Documentation": faIcon("train"),
  "International Pet Relocation": faIcon("truck-moving"),
  "Pet Insurance": faIcon("shield-halved"),
  "Pet Adoption Service": faIcon("paw"),
  "Pet Rescue Service": faIcon("hand-holding-heart"),
  "Pet Store": faIcon("store"),
  "Online Pet Store": faIcon("store"),
  "Pet Pharmacy": faIcon("pills"),
  "Pet Accessories Shop": faIcon("tags"),
  "Pet Purchase Shop": faIcon("shapes"),
  "Pet Photography": faIcon("camera"),
  "Pet Art": faIcon("palette"),
  "Pet Event Planning": faIcon("cake-candles"),
  "Pet Cafe": faIcon("film"),
  "Pet Cremation": faIcon("fire"),
  "Pet Funeral Service": faIcon("box-archive"),
  "Pet Cemetery": faIcon("seedling"),
  "Dog Breeding": faIcon("venus-mars"),
  "Pet Registration": faIcon("certificate"),
  "Pet Waste Cleaning Service": faIcon("spray-can-sparkles"),
};

const getServiceTypeIcon = (serviceName) =>
  SERVICE_TYPE_ICON_MAP[serviceName] || faIcon("stethoscope");

const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getHeroStyle = (categoryName) => {
  const base = CATEGORY_HERO_STYLES[categoryName] || {
    accent: "#2563eb",
    accentSoft: "#dbeafe",
    label: "P",
  };
  return {
    ...base,
    slug: slugify(categoryName),
  };
};

const normalizeCity = (value) => {
  const lower = String(value || "").trim().toLowerCase();
  if (!lower) return "";
  return CITY_ALIASES[lower] || lower;
};

const normalizeCategory = (value) => {
  const lower = String(value || "").trim().toLowerCase();
  if (!lower) return "";
  if (CATEGORY_ALIASES[lower]) return CATEGORY_ALIASES[lower];
  const hit = Object.entries(CATEGORY_ALIASES).find(([k]) => lower.includes(k));
  return hit ? hit[1] : lower;
};

const deterministicNumber = (seedText = "") => {
  const input = String(seedText || "");
  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
};

function toDisplayCity(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function ServiceLandingPage({
  city,
  category,
  user,
  selectedPet,
  onBack,
  onOpenProvider,
  onLoginClick,
  onOpenLanding,
}) {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );
  const [services, setServices] = useState([]);
  const [reviewStatsByService, setReviewStatsByService] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const cityKey = normalizeCity(city);
  const categoryKey = normalizeCategory(category);
  const hasCity = Boolean(cityKey);
  const displayCity = hasCity ? toDisplayCity(city) : "";
  const displayCategory = category || "Pet Services";
  const heroStyle = getHeroStyle(displayCategory);
  const heroImageSrc = `/category-heroes/${heroStyle.slug || "petaar-category"}.png`;
  const categoryIconImage = CATEGORY_ICON_IMAGES[displayCategory] || CATEGORY_ICON_IMAGES["Veterinary & Health Care"];
  const [heroImage, setHeroImage] = useState(heroImageSrc);

  useEffect(() => {
    setHeroImage(heroImageSrc);
  }, [heroImageSrc]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [cityKey, categoryKey]);

  useEffect(() => {
    const pageTitle = hasCity
      ? `${displayCategory} in ${displayCity} | Petaar`
      : `${displayCategory} | Petaar`;
    const pageDescription = hasCity
      ? `Discover ${displayCategory} providers in ${displayCity}. View verified profiles and book trusted pet services on Petaar.`
      : `Discover ${displayCategory} providers on Petaar. View verified profiles and book trusted pet services.`;
    document.title = pageTitle;
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute("content", pageDescription);
  }, [displayCategory, displayCity, hasCity]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setLoadError("");
        const snap = await getDocs(query(collection(db, "services"), where("isPublic", "==", true)));
        const rows = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        setServices(rows);

        const reviewSnap = await getDocs(collection(db, "reviews"));
        const totals = {};
        reviewSnap.docs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          if (data.hidden === true) return;
          const serviceId = String(data.serviceId || "").trim();
          const rating = Number(data.rating);
          if (!serviceId || !Number.isFinite(rating)) return;
          const current = totals[serviceId] || { sum: 0, count: 0 };
          current.sum += rating;
          current.count += 1;
          totals[serviceId] = current;
        });
        const normalized = {};
        Object.entries(totals).forEach(([serviceId, stat]) => {
          normalized[serviceId] = {
            avg: stat.count ? stat.sum / stat.count : 0,
            count: stat.count || 0,
          };
        });
        setReviewStatsByService(normalized);
      } catch (error) {
        setLoadError(error.message || "Unable to load landing page services.");
        setServices([]);
        setReviewStatsByService({});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [cityKey, categoryKey]);

  const filtered = useMemo(() => {
    return services.filter((service) => {
      const serviceCity = normalizeCity(service.city);
      const serviceCategory = normalizeCategory(service.category);
      const cityMatch = cityKey ? serviceCity === cityKey : true;
      const categoryMatch = categoryKey ? serviceCategory === categoryKey : true;
      return cityMatch && categoryMatch;
    });
  }, [services, cityKey, categoryKey]);

  const relatedCategories = useMemo(() => {
    return SERVICE_CATEGORIES
      .map((item) => item.name)
      .filter((name) => normalizeCategory(name) !== categoryKey)
      .slice(0, 5);
  }, [categoryKey]);

  const relatedCities = useMemo(() => {
    return MAJOR_INDIAN_CITIES
      .filter((name) => normalizeCity(name) !== cityKey)
      .slice(0, 8);
  }, [cityKey]);

  const categoryConfig = useMemo(() => {
    if (!categoryKey) return null;
    return SERVICE_CATEGORIES.find((item) => normalizeCategory(item.name) === categoryKey) || null;
  }, [categoryKey]);
  const serviceTypes = categoryConfig?.services || [];
  const isMobileView = viewportWidth < 760;

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fbff",
        padding: isMobileView ? "12px 10px 18px" : 24,
      }}
    >
      <button
        onClick={onBack}
        aria-label="Back to home"
        style={{
          width: isMobileView ? 50 : 58,
          height: isMobileView ? 50 : 58,
          borderRadius: "50%",
          border: "1px solid #dbe3ee",
          background: "#ffffff",
          boxShadow: "0 10px 20px rgba(15, 23, 42, 0.16)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: isMobileView ? 24 : 28,
          color: "#1e40af",
          fontWeight: 800,
        }}
      >
        ←
      </button>

      <section
        style={{
          marginTop: 12,
          background: "linear-gradient(135deg, #eef5ff 0%, #fff6e5 100%)",
          border: "1px solid #dbe3ee",
          borderRadius: 14,
          padding: isMobileView ? 12 : 20,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobileView ? "1fr" : "minmax(240px, 1.1fr) minmax(220px, 0.9fr)",
            gap: isMobileView ? 12 : 20,
            alignItems: "center",
          }}
        >
          <div>
            <h1 style={{ margin: "0 0 8px", color: "#0f172a", fontSize: isMobileView ? "clamp(30px, 7.8vw, 38px)" : "clamp(26px, 3vw, 36px)" }}>
              {displayCategory}
            </h1>
            <p style={{ marginTop: 0, color: "#334155", maxWidth: 720, fontSize: isMobileView ? 17 : 16, lineHeight: 1.4 }}>
              Book verified providers for {displayCategory.toLowerCase()}. Check service details and book online with secure checkout.
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              <span style={{ border: "1px solid #cbd5e1", borderRadius: 999, padding: "4px 10px", background: "#f8fafc" }}>
                Verified Providers
              </span>
              <span style={{ border: "1px solid #cbd5e1", borderRadius: 999, padding: "4px 10px", background: "#f8fafc" }}>
                Secure Payments
              </span>
              <span style={{ border: "1px solid #cbd5e1", borderRadius: 999, padding: "4px 10px", background: "#f8fafc" }}>
                Pan-India Network
              </span>
            </div>
          </div>
            <div
              style={{
                position: "relative",
                borderRadius: 18,
                border: "1px solid rgba(148,163,184,0.35)",
                background: "linear-gradient(135deg, #ffffff 0%, #e8f1ff 55%, #fdf4df 100%)",
                minHeight: isMobileView ? 178 : 220,
                overflow: "hidden",
                padding: isMobileView ? 10 : 18,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                color: heroStyle.accent,
                boxShadow: "0 20px 45px rgba(15,23,42,0.12)",
                transform: isMobileView ? "none" : "translateY(-6px)",
              }}
            >
              <img
                src={heroImage}
                alt={`${displayCategory} hero`}
                onError={() => setHeroImage(categoryIconImage)}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  objectPosition: "center",
                  background: "linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(135deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.15) 60%, rgba(15,23,42,0.08) 100%)",
                }}
              />
              <div
                style={{
                  position: "relative",
                  width: isMobileView ? 90 : 120,
                  height: isMobileView ? 90 : 120,
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.78)",
                  border: "1px solid rgba(255,255,255,0.8)",
                  display: heroImage === categoryIconImage ? "flex" : "none",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 12px 26px rgba(15,23,42,0.12)",
                }}
              >
                <img src={categoryIconImage} alt="" style={{ width: 68, height: 68 }} />
              </div>
          </div>
        </div>
      </section>

      {hasCity && (
        <section style={{ marginTop: 12, display: "none" }} />
      )}

      {serviceTypes.length > 0 && (
        <section style={{ marginTop: 18, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
          <h2 style={{ marginTop: 0 }}>Services Included</h2>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${isMobileView ? 180 : 230}px, 1fr))`, gap: 14, marginTop: 10 }}>
            {serviceTypes.map((item) => (
              <div
                key={`service-type-${item}`}
                style={{
                  border: "1px solid #bfdbfe",
                  borderRadius: 14,
                  padding: "14px 16px",
                  fontWeight: 800,
                  background: "#e0f2fe",
                  color: "#0f172a",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  boxShadow: "0 10px 20px rgba(15,23,42,0.08), inset 0 0 0 1px rgba(255,255,255,0.7)",
                }}
              >
                  <span
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: 18,
                    background: "#ffffff",
                    border: "1px solid #c7ddff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                      color: heroStyle.accent,
                    }}
                  >
                  <img src={getServiceTypeIcon(item)} alt="" style={{ width: 34, height: 34, objectFit: "contain" }} />
                </span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section style={{ marginTop: 18 }}>
        <h2 style={{ marginBottom: 8 }}>Available Providers</h2>
        {loadError && (
          <div style={{ border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", padding: 10, borderRadius: 8 }}>
            {loadError}
          </div>
        )}
        {loading ? (
          <p>Loading providers...</p>
        ) : filtered.length === 0 ? (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff", padding: 14 }}>
            {hasCity ? "No providers are live for this city-category combination yet." : "No providers are live for this category yet."}
            <div style={{ marginTop: 10 }}>
              <button onClick={onBack} style={SECONDARY_BUTTON_STYLE}>Explore all services</button>
            </div>
          </div>
        ) : (
          filtered.map((service) => {
            const reviewMeta = reviewStatsByService[service.id] || { avg: 0, count: 0 };
            const seed = deterministicNumber(service.id || service.title || service.city);
            const fallbackRating = [4.6, 4.7, 4.8, 4.9][seed % 4];
            const rating = reviewMeta.count ? Math.min(4.9, Math.max(4.6, Number(reviewMeta.avg) || fallbackRating)) : fallbackRating;
            const reviewCount = reviewMeta.count ? reviewMeta.count : 8 + (seed % 87);
            const providerPhoto =
              (Array.isArray(service.imageUrls) && service.imageUrls[0]) ||
              CATEGORY_ICON_IMAGES[service.category] ||
              "/petaar-logo.jpeg";
            return (
              <div
                key={service.id}
                style={{
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  borderRadius: 16,
                  padding: 16,
                  marginBottom: 10,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  position: "relative",
                }}
              >
                <button
                  type="button"
                  title="Save service"
                  style={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    border: "none",
                    background: "transparent",
                    color: PRIMARY_BLUE,
                    fontSize: 22,
                    lineHeight: 1,
                    cursor: "pointer",
                  }}
                >
                  ♡
                </button>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 12, alignItems: "start", paddingRight: 30 }}>
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      overflow: "hidden",
                      background: "#e8f1ff",
                    }}
                  >
                    <img src={providerPhoto} alt={service.title || "Provider"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                  <div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                      <span
                        style={{
                          border: "1px solid #a7f3d0",
                          color: "#047857",
                          background: "#ecfdf5",
                          borderRadius: 999,
                          padding: "2px 8px",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        ✓ Verified
                      </span>
                      <span style={{ color: META_COLOR, fontSize: 13, fontWeight: 700 }}>
                        ★ {rating.toFixed(1)} ({reviewCount} reviews)
                      </span>
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: HEADING_COLOR, marginBottom: 6 }}>
                      {service.title || "Service"}
                    </div>
                    <div style={{ color: META_COLOR, fontSize: 13, marginBottom: 8 }}>
                      {(service.category || displayCategory)} | {toDisplayCity(service.city || displayCity)} | {service.availabilityNote || "Mon-Sat"}
                    </div>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        borderRadius: 999,
                        background: "#E8EEFF",
                        color: PRIMARY_BLUE,
                        padding: "6px 12px",
                        fontWeight: 700,
                        fontSize: 13,
                        marginBottom: 12,
                      }}
                    >
                      Starting from ₹{Number(service.price || 0).toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                  {user ? (
                    <RazorpayButton
                      service={service}
                      selectedPet={selectedPet}
                      blockedDates={Array.isArray(service?.unavailableDates) ? service.unavailableDates : []}
                      blockedSlots={Array.isArray(service?.unavailableSlots) ? service.unavailableSlots : []}
                      buttonLabel="Book Now"
                      buttonStyle={PRIMARY_BUTTON_STYLE}
                    />
                  ) : (
                    <button onClick={() => onLoginClick(service)} style={PRIMARY_BUTTON_STYLE}>Book Now</button>
                  )}
                  <button onClick={() => onOpenProvider(service)} style={SECONDARY_BUTTON_STYLE}>View Details</button>
                </div>
              </div>
            );
          })
        )}
      </section>

      {onOpenLanding && (
        <section style={{ marginTop: 20, border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff", padding: 14 }}>
          <h3 style={{ marginTop: 0 }}>Explore Related Pages</h3>
          {hasCity ? (
            <>
              <div style={{ marginBottom: 10 }}>
                <small style={{ color: "#64748b", fontWeight: 700 }}>Same city, more services</small>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                  {relatedCategories.map((item) => (
                    <button
                      key={`related-category-${item}`}
                      onClick={() => onOpenLanding({ city: displayCity, category: item })}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <small style={{ color: "#64748b", fontWeight: 700 }}>Same service, other cities</small>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                  {relatedCities.map((item) => (
                    <button
                      key={`related-city-${item}`}
                      onClick={() => onOpenLanding({ city: item, category: displayCategory })}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div>
              <small style={{ color: "#64748b", fontWeight: 700 }}>Choose a city</small>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                {relatedCities.map((item) => (
                  <button
                    key={`related-city-${item}`}
                    onClick={() => onOpenLanding({ city: item, category: displayCategory })}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
